import { takeLatest, put, select, call } from 'redux-saga/effects';

import { pending, success, error } from './saveReducer';
import {
  currentRecordSelector,
  eventInfoSelector,
  eventStatusSelector,
} from './selectors';
import { EventState } from './eventStatusReducer';
import { saveRecord } from '../services';
import { currentRecordToFields } from '../format';
import type { CurrentRecordState } from './index';
import { captureException } from 'shared/utils/handlerErrors';

export const STORAGE_KEY = '@iclinic/medicalRecords/currentRecord';
export const EVENTID_KEY = '@iclinic/medicalRecords/eventId';

export function* persistLocal(
  currentRecord: CurrentRecordState,
  eventId: number,
) {
  try {
    yield call([localStorage, 'setItem'], EVENTID_KEY, eventId);
    yield call(
      [localStorage, 'setItem'],
      STORAGE_KEY,
      JSON.stringify(currentRecord),
    );
  } catch (err: any) {
    yield put(error(err.message));
    yield call(captureException, err);
  }
}

export function* saveRequest(
  currentRecord: CurrentRecordState,
  eventId: number,
  patientId: number,
) {
  try {
    yield put(pending());

    const fields = currentRecordToFields(currentRecord);
    yield call(saveRecord, eventId, patientId, fields);

    yield put(success());
  } catch (err: any) {
    yield put(error(err.message));
    yield call(captureException, err);
  }
}

const currentRecordRegex = /medicalRecords\/currentRecord.*/;

function* saveCurrentRecordSaga() {
  let previousCurrentRecord: CurrentRecordState = yield select(
    currentRecordSelector,
  );

  yield takeLatest(
    (action: any) => currentRecordRegex.test(action.type),
    function* saveCurrentRecord() {
      try {
        const eventStatus: EventState = yield select(eventStatusSelector);

        if (eventStatus === EventState.IDLE) {
          return;
        }

        const currentRecord: CurrentRecordState = yield select(
          currentRecordSelector,
        );
        const {
          eventId,
          patient: { id: patientId },
        } = yield select(eventInfoSelector);

        if (currentRecord !== previousCurrentRecord) {
          previousCurrentRecord = currentRecord;

          yield call(persistLocal, currentRecord, eventId);
          yield call(saveRequest, currentRecord, eventId, patientId);
        }
      } catch (err) {
        yield call(captureException, err);
      }
    },
  );
}

export default saveCurrentRecordSaga;
